import {Action, createFeatureSelector, createSelector, select} from '@ngrx/store';
import {Injectable} from '@angular/core';
import {filter, map} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {BaseState} from '../../core/base.state';
import * as _ from 'lodash';

export const FEATURE_NAME = 'UI_MODAL';
export interface ITemplate {
    name: string;
    templateRef: any;
    componentRef: any;
    selected: boolean;
}

export interface IModalNavigation {
    current: ITemplate;
    previous: ITemplate
}

enum ActionTypes {

    Set = "[UI_MODAL] Set",
    Success = "[UI_MODAL] Success",
    Failure = "[UI_MODAL] Failure",


    Open = "[UI_MODAL] Open Modal",
    Close = "[UI_MODAL] Close Modal",
    ForceClose = "[UI_MODAL] Force Close Modal",
    Options = "[UI_MODAL] Set Options",
    TemplateRef = "[UI_MODAL] Set Modal Template Ref",

    //Navigation
    Pop = "[UI_MODAL] Navigation: Pop TemplateRef",
    Select = "[UI_MODAL] Navigation: Select",
    Previous = "[UI_MODAL] Navigation: Previous"


}

class Set implements Action {
    readonly type = ActionTypes.Set;
    constructor(public payload: any) { }
}

class Success implements Action {
    readonly type = ActionTypes.Success;
    constructor(public payload: any) { }
}

class Failure implements Action {
    readonly type = ActionTypes.Failure;
    constructor(public payload: any) { }
}

class Select implements Action {
    readonly type = ActionTypes.Select;
    constructor(public payload: any) { }
}

class Open implements Action {
    readonly type = ActionTypes.Open;
}

class Pop implements Action {
    readonly type = ActionTypes.Pop;
}

class Previous implements Action {
    readonly type = ActionTypes.Previous;
}

class Close implements Action {
    readonly type = ActionTypes.Close;
}

class ForceClose implements Action {
    readonly type = ActionTypes.ForceClose;
    constructor(public payload: any) { }
}

class Options implements Action {
    readonly type = ActionTypes.Options;
    constructor(public payload: any) { }
}

class TemplateRef implements Action {
    readonly type = ActionTypes.TemplateRef;
    constructor(public payload: any) { }
}

type ActionsUnion =
    | Set
    | Failure
    | Success
    | Open
    | Close
    | ForceClose
    | Options
    | Pop
    | Previous
    | Select
    | TemplateRef;


interface IState {
    templateRef: Array<any>;
    options: any;
    isOpened: boolean;
    success: boolean;
    reason: string;
    forceClose: boolean;
    navigation: IModalNavigation;
}

const initialState: IState = {
    templateRef: [],
    options: undefined,
    isOpened: undefined,
    success: undefined,
    reason: undefined,
    forceClose: undefined,
    navigation: {
        current: undefined,
        previous: undefined
    }
};

const selectState = createFeatureSelector<IState>(FEATURE_NAME);

// Effects

// Reducer

export function reducer(state = initialState, action: ActionsUnion): IState {

    switch (action.type) {

        case ActionTypes.Set: {
            return {
                ...state,
                ...action.payload
            }
        }

        case ActionTypes.Success: {
            return {
                ...state,
                success: true
            }
        }

        case ActionTypes.Failure: {
            return {
                ...state,
                success: false,
                reason: action.payload.reason
            }
        }

        case ActionTypes.Open: {
            return {
                ...state,
                isOpened: true
            }
        }

        case ActionTypes.Close: {
            return {
                ...state,
                isOpened: false
            }
        }

        case ActionTypes.ForceClose: {
            return {
                ...state,
                forceClose: true
            }
        }

        case ActionTypes.Pop: {
            return {
                ...state,
                templateRef: state.templateRef.slice(0, -1)
            }
        }

        case ActionTypes.Previous: {
            return {
                ...state,
                navigation: {
                    current: state.navigation.previous,
                    previous: state.navigation.current
                }
            }
        }

        case ActionTypes.Select: {
            return {
                ...state,
                navigation: {
                    current: _.find(state.templateRef, <any>{name: action.payload}),
                    previous: state.navigation.current
                }
            }
        }

        case ActionTypes.Options: {
            return {
                ...state,
                options: action.payload
            }
        }

        case ActionTypes.TemplateRef: {
            return {
                ...state,
                templateRef: _.uniqBy(_.flattenDeep([...state.templateRef, [action.payload]]), 'name')
            }
        }

        default: {
            return state;
        }
    }
}


// Feature Selectors and State

const selectors = {
    success: createSelector(selectState, (state: IState) => state.success),
    options: createSelector(selectState, (state: IState) => state.options),
    templateRef: createSelector(selectState, (state: IState) => state.templateRef),
    isOpened: createSelector(selectState, (state: IState) => state.isOpened),
    forceClose: createSelector(selectState, (state: IState) => state.forceClose),
    navigation: createSelector(selectState, (state: IState) => state.navigation)
};


@Injectable()
export class State extends BaseState {

    get success(): Observable<boolean> {
        return this._store.pipe(
            select(selectors.success),
            filter(success => success !== undefined)
        )
    }

    get isOpened(): Observable<boolean> {
        return this._store.pipe(
            select(selectors.isOpened),
            filter(success => success !== undefined)
        )
    }

    get options(): any {
        return this._store.pipe(
            select(selectors.options),
            filter(success => success !== undefined)
        )
    }

    set options(options) {
        this._store.dispatch(new Options(options))
    }

    get templateRef(): Observable<any> {
        return this._store.pipe(
            select(selectors.templateRef),
            filter(templateList => !_.isEmpty(templateList))
        )
    }

    set templateRef(t) {
        this._store.dispatch(new TemplateRef(t))
    }

    unload() {
        this._store.dispatch(new Set({isOpened: undefined, navigation: { current: undefined }}))
    }

    open() {
        this._store.dispatch(new Open())
    }

    close() {
        this._store.dispatch(new Close())
    }

    set forceClose(forceClose) {
        this._store.dispatch(new Set({forceClose: true}));
        setTimeout(() => {
            this._store.dispatch(new Set({ forceClose: undefined, isOpened: undefined }))
        }, 200)

    }

    get forceClose(): Observable<any> | any {
        return this._store.pipe(
            select(selectors.forceClose),
            filter(success => success !== undefined)
        )
    }


    selectedItemIds: any;
    set setSelectedItemIds(selectedItemIds) {
        this.selectedItemIds = selectedItemIds;
    }

    get getSelectedItemIds() {
        return this.selectedItemIds;
    }

    objectToDeleteItem: any;
    set setObjectToDeleteItem(objectToDeleteItem) {
        this.objectToDeleteItem = objectToDeleteItem;
    }

    get getObjectToDeleteItem() {
        return this.objectToDeleteItem;
    }

    newUserInformation: any;
    set setNewUserInformation(newUserInformation) {
        this.newUserInformation = newUserInformation;
    }

    get getNewUserInformation() {
        return this.newUserInformation;
    }

    navigation = {
        push: (templateRef) => {
            this.templateRef = templateRef;
        },
        previous: () => {
            this._store.dispatch(new Previous())
        },
        select: (templateName) => {
            this._store.dispatch(new Select(templateName));
        },
        current: () => {
            return this._store.pipe(
                select(selectors.navigation),
                map(navigation => navigation.current),
                filter(current => current !== undefined)
            )
        }

    }

}
